import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Link } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import VideoLibrary from '@mui/icons-material/VideoLibrary';
import { GeneralDialog } from '../common/dialog/GeneralDialog';
import { ClassContext } from '../../context/ClassContext';
import { useSchoolInfo } from '../../hooks/useSchoolInfo';
import { getRecordingArtifactURLs } from './util';
import { RecordingDialogProps } from './types';

export const RecordingLinksDialog = (props: RecordingDialogProps) => {
    const { t } = useTranslation();
    const { open, downloading, recording, handleDialogClosed } = props;
    const [loading, setLoading] = useState(true);
    const [artifacts, setArtifacts] = useState<Record<string, string>>({});
    const [mediaError, setMediaError] = useState<string | undefined>();
    const { userUuid, classUuid } = useContext(ClassContext);
    const { data: schoolInfo } = useSchoolInfo();

    useEffect(() => {
        document.title = t(downloading ? 'lti:recordings.downloadmedia' : 'lti:recordings.external-link');
        setMediaError(undefined);

        if (open && schoolInfo?.uuid && userUuid && classUuid && recording?.recordingId) {
            const applyArtifacts = async () => {
                const artifactURLsByContentType = await getRecordingArtifactURLs(
                    schoolInfo?.uuid,
                    userUuid,
                    classUuid,
                    recording.recordingId
                );
                setArtifacts(artifactURLsByContentType ?? {});
                setLoading(false);
            };
            setLoading(true);
            applyArtifacts();
        } else {
            setArtifacts({});
            setLoading(false);
        }
    }, [downloading, recording, userUuid, schoolInfo, classUuid, t, open]);

    return (
        <GeneralDialog
            onClose={handleDialogClosed}
            open={open}
            isLoading={loading}
            title={t(downloading ? 'lti:recordings.downloadmedia' : 'lti:recordings.external-link')}
            actions={
                <Button autoFocus onClick={() => handleDialogClosed()} variant="contained" color="primary">
                    {t('common:close')}
                </Button>
            }
        >
            {!loading &&
                (Object.keys(artifacts).length > 0 ? (
                    Object.entries(artifacts).map(([contents, url]) => (
                        <Link key={contents} underline="hover" sx={{ my: 2, display: 'block' }} href={url}>
                            {downloading ? <DownloadIcon /> : <VideoLibrary />}
                            <Box sx={{ mt: '-30px', ml: '30px' }}>{t(`lti:recordings.mediatypes.${contents}`)}</Box>
                        </Link>
                    ))
                ) : (
                    <Alert severity="info">
                        {t('lti:recordings.nomedia')}
                        {mediaError && <Box sx={{ mt: 1 }}>{mediaError}</Box>}
                    </Alert>
                ))}
        </GeneralDialog>
    );
};
